import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getAllBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadAllBrands from "@insite/client-framework/Store/Pages/Brands/Handlers/LoadAllBrands";
import translate from "@insite/client-framework/Translate";
import { BrandModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, injectCss } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import colorPallete, { bodyFonts, elevation, headlineFonts, labelFonts } from "@dana/MobiusOverrides/styles/variables";
import Carousel, { CarouselSlide } from "@dana/Overrides/Components/Carousel";
import React, { createRef, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    title = "title",
    description = "description",
    brandsToDisplay = "brandsToDisplay",
    numberOfBrandsToDisplay = "numberOfBrandsToDisplay",
    brandsSelected = "brandsSelected",
    titleAlignment = "titleAlignment",
    path = "path",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;       
        [fields.description]: string;
        [fields.brandsToDisplay]: "random" | "select-brands";
        [fields.numberOfBrandsToDisplay]: string;
        [fields.brandsSelected]: string[];
        [fields.titleAlignment]: string;
        [fields.path]: string;
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const brands = getAllBrandsDataView(state).value;
    return {
        brands,
    };
};

const mapDispatchToProps = {
    loadAllBrands,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

export interface PrftBrandCarouselStyles {
    errorMessageText?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    container?: GridContainerProps;
    innerContainer?: GridContainerProps;
    brandLinkImage?: InjectableCss;   
    image?: LazyImageProps;
    titleSubText?: TypographyPresentationProps;
    viewMoreLinkItem?: InjectableCss;
    viewMoreLink?: LinkPresentationProps;
}

export const ContentWrapper = styled.div<InjectableCss & { alignment: string }>`
    display: flex;
    width: 100%;
    justify-content: ${props => props.alignment ?? "flex-start"};
    ${injectCss};
`;

export const PrftBrandCarouselStyles: PrftBrandCarouselStyles = {
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },
    titleText: {
        variant: "h4",
        weight: headlineFonts.headline4SemiBoldLg.fontWeight,
        css: css`
            text-align: center;
            font-size: ${headlineFonts.headline1SemiBoldSm.fontSize};
            line-height: ${headlineFonts.headline1SemiBoldSm.lineHeight};
            @media only screen and (max-width: 768px) {
                font-size: ${headlineFonts.headline3SemiBoldSm.fontSize};
                line-height: ${headlineFonts.headline3SemiBoldSm.lineHeight};
                margin-bottom: 26px;
            }
        `,
    },
    mainContainer: {
        gap: 0,
        offsetCss: css`
            @media only screen and (max-width: 768px) {
                justify-content: flex-end;
            }
            max-width: 1420px;
            margin-left: auto;
            margin-right: auto;
        `,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            order: -1;
            justify-content: flex-start;           
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                        display: none;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                        display: none;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
            @media only screen and (max-width: 768px) {
                display: none;
            }
        `,
    },
    prevArrowButton: {
        shape: "rounded",
        css: css`
            padding: 0;
            min-width: 40px;
            min-height: 40px;
            border: 1px solid ${colorPallete.grey200};
            background: ${colorPallete.white};
            color: ${colorPallete.grey800};
            border-radius: 48px;
            box-shadow: ${elevation.dp04};
            z-index: 2;

            [class*="ButtonIcon"] {
                color: ${colorPallete.grey800};
            }

            &:not(:disabled):hover {
                background: ${colorPallete.white};
                color: ${colorPallete.black};
                border: 1px solid ${colorPallete.grey100};

                [class*="ButtonIcon"] {
                    color: ${colorPallete.black};
                }
            }
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
            
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                        display: none;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                        display: none;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
            @media only screen and (max-width: 768px) {
                display: none;
            }
        `,
    },
    nextArrowButton: {
        shape: "rounded",
        css: css`
            padding: 0;
            min-width: 40px;
            min-height: 40px;
            border: 1px solid ${colorPallete.grey200};
            background: ${colorPallete.white};
            color: ${colorPallete.grey800};
            border-radius: 48px;
            z-index: 2;
            box-shadow: ${elevation.dp04};
            [class*="ButtonIcon"] {
                color: ${colorPallete.grey800};
            }

            &:not(:disabled):hover {
                background: ${colorPallete.white};
                color: ${colorPallete.black};
                border: 1px solid ${colorPallete.grey100};

                [class*="ButtonIcon"] {
                    color: ${colorPallete.black};
                }
            }
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                ])}
            @media only screen and (min-width: 768px) {
                &[data-test-selector="carouselGridItem_crossSells"],
                &[data-test-selector="carouselGridItem_recentlyViewed"] {
                    border-bottom: 1px solid ${colorPallete.grey300};
                    padding-bottom: 10px;
                }
            }
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;

            img {
                height: auto;
            }
            &.lessItemsThanSlideToScroll {
                transform: initial !important;
                justify-content: center;
            }
            > a {
                &:focus {
                    outline-offset: -2px;
                }
                &:focus:not(:focus-visible) {
                    outline-offset: 0;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                    gap: 0;
                    > a {
                        margin-left: 20px;
                        margin-right: 20px;
                    }
                `,
                ], "min")}
            @media print {
                > a {
                    width: calc(100% / 3 - 40px);
                    padding: 20px;
                    text-align: center;
                    vertical-align: middle;
                    display: inline-block;
                    margin: 20px;
                }
            }
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;

            @media only screen and (max-width: 768px) {
                min-width: 200px;
                max-width: 200px;
                margin-right: 16px;
            }
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0 20px;
            display: flex;
            flex-direction: column;
            @media only screen and (max-width: 768px) {
                padding-left: 0;
                padding-right: 0;
            }
        `,
    },
    brandLinkImage: {
        css: css`
            padding: 24px;
            img {
                width: 146px;
            }
        `,
    },
    
    titleSubText: {
        size: bodyFonts.body2RegularSm.fontSize,
        lineHeight: bodyFonts.body2RegularSm.lineHeight,
        color: colorPallete.grey600,
        css: css`
            text-align: center;
            margin-bottom: 20px;
        `,
    },
    viewMoreLinkItem: {
        css: css`
            text-align: center;
            font-weight: bold;
            justify-content: center;
            margin-top: 20px;
        `,
    },
    viewMoreLink: {
        css: css`
            min-width: 278px;
            display: inline-flex;
            justify-content: center;
            > span[class*="StyledTypography"] {
                color: ${colorPallete.primaryRed500};
            }
            &:hover {
                > span[class*="StyledTypography"] {
                    color: ${colorPallete.white};
                }
            }
        `,
    },
};

const styles = PrftBrandCarouselStyles;

const PrftBrandCarousel = ({
    id,
    fields,
    brands,
    loadAllBrands,
}: Props) => {
    const [slides, setSlides] = useState<CarouselSlide[]>([]);
    const [areBrandsAvailable, setAreBrandsAvailable] = useState(false);
    const afterCarousel = createRef<HTMLSpanElement>();

    useEffect(() => {
        if (!brands || brands.length === 0) {
            loadAllBrands({
                sort: "name asc",
                select: "id,name,detailPagePath,logoSmallImagePath,logoAltText",
            });
        }
    }, [loadAllBrands]);

    useEffect(() => {
        if (brands && brands.length > 0) {
            let brandMutable = [...brands];
            let count = parseInt(fields.numberOfBrandsToDisplay, 10) || 0;
            count = count > brandMutable.length ? brandMutable.length : count;
 
            if (fields.brandsToDisplay === "random" && fields.numberOfBrandsToDisplay !== "All" && count <= brandMutable.length) {
                brandMutable = brandMutable.sort(() => Math.random() - 0.5).slice(0, count);
            }else if(fields.brandsToDisplay === "random" && fields.numberOfBrandsToDisplay === "All"){
                brandMutable = [...brands]
            }
            else if (fields.brandsToDisplay === "select-brands") {
                brandMutable = fields.brandsSelected.map(brandId => brandMutable.find(brand => brand.id === brandId)).filter(Boolean) as BrandModel[];
            }
 
            if (brandMutable.length > 0) {
                setAreBrandsAvailable(true);
            } else {
                setAreBrandsAvailable(false);
            }
 
            setSlides(mapProductsToSlides(brandMutable));
        } else {
            setAreBrandsAvailable(false);
        }
    }, [brands]);

    const mapProductsToSlides = (brands: BrandModel[]) =>
        brands.map(brand => ({
            id,
            renderComponent: function PrftCategoryCarouselCategoryRenderer() {
                return (
                    <GridContainer {...styles.container} id={`${brand.id}-brand-container`}>
                        <GridItem {...styles.innerContainer}>
                            {brand.detailPagePath && (
                                <Link href={brand.detailPagePath} title={brand.name} {...styles.brandLinkImage}>
                                    <LazyImage altText={brand.logoAltText} src={brand.logoSmallImagePath} {...styles.image} />
                                </Link>
                            )}
                        </GridItem>
                    </GridContainer>
                );
            },
        }));


    return (
         <>
         {areBrandsAvailable && 
        (<StyledWrapper {...styles.mainContainer}>
        
                <SkipNav
                    text={translate("Skip Carousel")}
                    extendedStyles={styles.skipCarouselButton}
                    destination={afterCarousel}
                />
                <StyledWrapper {...styles.container}>
                    <ContentWrapper alignment={fields.titleAlignment} className="wrapper">
                        <Typography {...styles.titleText} variant="h3">
                            {translate(fields.title || "Featured Brands")}
                        </Typography>
                    </ContentWrapper>
                    {fields.description !== "" &&
                    (<ContentWrapper alignment={fields.titleAlignment} className="wrapper">
                        <Typography {...styles.titleSubText}>{fields.description}</Typography>
                    </ContentWrapper>)}
                </StyledWrapper>
                <StyledWrapper {...styles.carouselContainer}>
                    <Carousel
                        id="BrandsCarousel"
                        maxNumberOfColumns={5}
                        title=""
                        slides={slides}
                        extendedStyles={PrftBrandCarouselStyles}
                    />
                </StyledWrapper>
                {fields.path !== "" && (
                <StyledWrapper {...styles.viewMoreLinkItem}>
                    <Link href={fields.path} {...styles.viewMoreLink} className="btn btn-secondary">
                        {translate("View all brands")}
                    </Link>
                </StyledWrapper>
                )}
           
        </StyledWrapper>
        )}
        </>
    );
};

const CarouselSlide = styled.div`
    padding: 0;
    position: relative;
    flex: 0 0 auto;
`;

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(PrftBrandCarousel)),
    definition: {
        group: "Common",
        icon: "image",
        displayName: "Perficient Brand Carousel",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Brands",
                fieldType: "Contextual",
            },
            {
                name: fields.titleAlignment,
                displayName: "Title Alignment",
                editorTemplate: "RadioButtonsField",
                defaultValue: "center",
                horizontal: true,
                fieldType: "General",
                sortOrder: 4,

                options: [
                    {
                        displayName: "Left",
                        value: "flex-start",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "flex-end",
                    },
                ],
            },
            {
                name: fields.description,
                displayName: "Description",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
            {
                name: fields.brandsToDisplay,
                displayName: "Brands to display",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Random",
                        value: "random",
                    },
                    {
                        displayName: "Select brands",
                        value: "select-brands",
                    },
                ],
                defaultValue: "random",
                horizontal: true,
                fieldType: "Contextual",
                tooltip:
                    "Brand logos can be picked at random on page load, or you can select specific brands to display.",
            },
            {
                name: fields.numberOfBrandsToDisplay,
                displayName: "Number of brands to display",
                editorTemplate: "DropDownField",
                defaultValue: "All",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                    { displayName: "9", value: "9" },
                    { displayName: "10", value: "10" },
                    { displayName: "11", value: "11" },
                    { displayName: "12", value: "12" },
                    { displayName: "13", value: "13" },
                    { displayName: "14", value: "14" },
                    { displayName: "15", value: "15" },
                    { displayName: "16", value: "16" },
                    { displayName: "17", value: "17" },
                    { displayName: "18", value: "18" },
                    { displayName: "All", value: "All" }
                ],
                fieldType: "Contextual",
                isVisible: item => item.fields.brandsToDisplay === "random",
            },
            {
                name: fields.brandsSelected,
                displayName: "Select brands",
                editorTemplate: "SelectBrandsField",
                isVisible: item => item.fields.brandsToDisplay === "select-brands",
                defaultValue: [],
                fieldType: "Contextual",
            },   
            {
                name: fields.path,
                displayName: "Brands Url",
                editorTemplate: "TextField",
                defaultValue: "/brands",
                fieldType: "Contextual",
            },
        ],
    },
};

export default widgetModule;
